import { css } from '@emotion/react';

export const usersManagementCSS = () => css`
    font-family: 'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #0f172a;

    .um-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1rem;
        flex-wrap: wrap;
    }

    .um-title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #0f172a;
        letter-spacing: 0.02em;
        margin: 0;
    }

    .um-subtitle {
        font-size: 0.78rem;
        color: #64748b;
        margin: 0.15rem 0 0;
    }

    .um-tabs {
        margin-bottom: 0.85rem;

        .p-tabview-nav-container a {
            color: #475569 !important;
            font-size: 0.8rem !important;
            font-weight: 600 !important;
        }
        .p-tabview-nav .p-tabview-nav-link {
            background: transparent !important;
            border-color: transparent transparent #e2e8f0 transparent !important;
        }
        .p-tabview-nav .p-highlight .p-tabview-nav-link {
            border-color: transparent transparent #00BA6E transparent !important;
            color: #00BA6E !important;
        }
        .p-tabview-panels {
            padding: 0.85rem 0 0 0 !important;
            background: transparent !important;
        }
    }

    /* ─── KPI chips (one-row, Dashboard-style) ─── */
    .um-kpis {
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: 0.6rem;
        margin: 0 0 1rem;
    }

    .um-kpis--trim {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media (max-width: 1050px) {
        .um-kpis,
        .um-kpis--trim { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }
    @media (max-width: 520px) {
        .um-kpis,
        .um-kpis--trim { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }

    .um-kpi {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.65rem;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        padding: 0.8rem 0.85rem;
        cursor: pointer;
        transition: transform 0.18s, border-color 0.18s, box-shadow 0.18s, background 0.18s;
        text-align: left;
        font-family: inherit;
        color: #0f172a;
        overflow: hidden;
        box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.04);
    }
    .um-kpi:hover {
        transform: translateY(-2px);
        box-shadow: 
            inset 3px 0 0 transparent,
            0 8px 18px rgba(33, 88, 140, 0.08);
    }

    .um-kpi-icon {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
    }

    .um-kpi-body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        gap: 2px;
    }
    .um-kpi-label {
        font-size: 0.62rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: #64748b;
        line-height: 1.1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .um-kpi-value {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.015em;
        color: #0f172a;
        margin-top: 2px;
    }

    @media (max-width: 520px) {
        .um-kpi-icon { width: 30px; height: 30px; font-size: 0.85rem; }
        .um-kpi-value { font-size: 1.25rem; }
        .um-kpi { padding: 0.65rem 0.7rem; gap: 0.5rem; }
    }

    ${(() => {
        const TONES = {
            all:        { accent: '#204B7B', soft: '#eef2f7' },
            pei_admin:  { accent: '#204B7B', soft: '#e8eef6' },
            pei_vend:   { accent: '#00BA6E', soft: '#e6f9f1' },
            trim_admin: { accent: '#34C6F1', soft: '#e3f5fb' },
            trim_vend:  { accent: '#1FDD8F', soft: '#e8fbf1' },
            clients:    { accent: '#94a3b8', soft: '#f1f5f9' },
            approved:   { accent: '#00BA6E', soft: '#e6f9f1' },
            cancelled:  { accent: '#94a3b8', soft: '#f1f5f9' },
            progress:   { accent: '#34C6F1', soft: '#e3f5fb' },
        };
        return Object.entries(TONES).map(([k, t]) => `
            .um-kpi.tone-${k}:hover { 
                border-color: ${t.accent}; 
                box-shadow: inset 3px 0 0 ${t.accent}, 0 8px 18px rgba(33, 88, 140, 0.08);
            }
            .um-kpi.tone-${k}.active {
                border-color: ${t.accent};
                background: ${t.soft};
                box-shadow: inset 3px 0 0 ${t.accent}, 0 6px 16px ${t.accent}26;
            }
            .um-kpi.tone-${k} .um-kpi-icon { color: ${t.accent}; background: ${t.soft}; }
            .um-kpi.tone-${k}.active .um-kpi-icon { background: #ffffff; }
        `).join('\n');
    })()}

    .um-toolbar {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        margin-bottom: 0.85rem;
        flex-wrap: wrap;
    }

    .um-search {
        flex: 1;
        min-width: 240px;
        max-width: 360px;
        position: relative;
    }
    .um-search-icon {
        position: absolute;
        right: 1.1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #94a3b8;
        font-size: 0.85rem;
        pointer-events: none;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .um-search input {
        width: 100%;
        padding: 0.55rem 2.2rem 0.55rem 1rem;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #0f172a;
        font-size: 0.85rem;
        font-family: inherit;
        outline: none;
        transition: border-color 0.18s, box-shadow 0.18s;
    }
    .um-search input:focus {
        border-color: #34C6F1;
        box-shadow: 0 0 0 3px rgba(52, 198, 241, 0.18);
    }

    /* ─── Skeleton placeholders ─── */
    .um-kpi-skeleton {
        cursor: default;
        pointer-events: none;
    }
    .um-kpi-skeleton:hover {
        transform: none;
        box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.04);
        border-color: #e2e8f0;
    }
    .um-row-skeleton {
        cursor: default !important;
    }
    .um-row-skeleton:hover {
        background: transparent !important;
    }
    .um-row-skeleton td {
        padding: 14px 20px;
    }

    .um-table-card {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
        border: 1px solid #e2e8f0;
        overflow: hidden;
    }

    .um-table-scroll {
        overflow-x: auto;
    }

    .um-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
    }

    .um-table thead tr {
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    .um-table th {
        padding: 14px 20px;
        font-size: 0.7rem;
        font-weight: 700;
        color: #475569;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        white-space: nowrap;
    }

    .um-table tbody tr {
        transition: background-color 0.12s;
        cursor: pointer;
    }

    .um-table tbody tr:hover {
        background: rgba(248, 250, 252, 0.85);
    }

    .um-table tbody tr:not(:last-child) {
        border-bottom: 1px solid #f1f5f9;
    }

    .um-table td {
        padding: 12px 20px;
        font-size: 0.82rem;
        color: #333333;
        vertical-align: middle;
    }

    .um-pagination {
        padding: 12px 20px;
        border-top: 1px solid #e2e8f0;
        background: #f8fafc;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .um-empty {
        padding: 3rem 1rem;
        text-align: center;
        color: #94a3b8;
        font-size: 0.88rem;
    }

    .um-empty i {
        display: block;
        font-size: 1.6rem;
        margin-bottom: 0.5rem;
        opacity: 0.55;
    }

    @media (max-width: 650px) {
        .um-header { flex-direction: column; align-items: flex-start; }
        .um-search { max-width: 100%; }
        .um-table th, .um-table td { padding: 10px 12px; }
    }
`;

export const roleChipCSS = (typeUser) => {
    const palette = {
        '1': { bg: '#204B7B', color: '#ffffff', border: 'transparent' },
        '2': { bg: '#00BA6E', color: '#ffffff', border: 'transparent' },
        '3': { bg: '#34C6F1', color: '#0f172a', border: 'transparent' },
        '4': { bg: 'transparent', color: '#00BA6E', border: '1px solid #00BA6E' },
        null: { bg: '#f1f5f9', color: '#64748b', border: 'transparent' },
    };
    const key = typeUser ?? null;
    const c = palette[key] ?? palette[null];
    return css`
        display: inline-flex;
        align-items: center;
        background: ${c.bg};
        color: ${c.color};
        border: ${c.border};
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        padding: 3px 10px;
        border-radius: 999px;
        white-space: nowrap;
    `;
};

export const stateChipCSS = (active) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: ${active ? '#f0fdf4' : '#f1f5f9'};
    color: ${active ? '#00BA6E' : '#94a3b8'};
    font-size: 0.7rem;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 999px;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${active ? '#00BA6E' : '#94a3b8'};
    }
`;

export const userDetailModalCSS = () => css`
    .udm-header {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        padding-bottom: 0.85rem;
        border-bottom: 1px solid #e2e8f0;
        margin-bottom: 0.6rem;
    }

    .udm-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: linear-gradient(135deg, #204B7B 0%, #00BA6E 100%);
        color: #ffffff;
        font-weight: 700;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        letter-spacing: 0.02em;
    }

    .udm-identity {
        flex: 1;
        min-width: 0;
    }

    .udm-name {
        font-size: 0.95rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .udm-email {
        font-size: 0.78rem;
        color: #64748b;
        margin: 0.1rem 0 0;
    }

    .udm-chips {
        display: flex;
        gap: 0.4rem;
        margin-top: 0.45rem;
        flex-wrap: wrap;
    }

    .udm-tabs {
        .p-tabview-nav-container a {
            color: #475569 !important;
            font-size: 0.78rem !important;
            font-weight: 600 !important;
            padding: 0.6rem 1rem !important;
        }
        .p-tabview-nav .p-tabview-nav-link {
            background: transparent !important;
            border-color: transparent transparent #e2e8f0 transparent !important;
        }
        .p-tabview-nav .p-highlight .p-tabview-nav-link {
            border-color: transparent transparent #00BA6E transparent !important;
            color: #00BA6E !important;
        }
        .p-tabview-panels {
            padding: 1rem 0 0 0 !important;
            background: transparent !important;
        }
    }

    .udm-section-title {
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
        margin: 0 0 0.6rem;
    }

    .udm-grid-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.85rem 1rem;

        @media (max-width: 650px) {
            grid-template-columns: 1fr;
        }
    }

    .udm-readonly-field {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .udm-readonly-label {
        font-size: 0.72rem;
        font-weight: 600;
        color: #475569;
        letter-spacing: 0.03em;
    }

    .udm-readonly-value {
        font-size: 0.85rem;
        color: #0f172a;
        word-break: break-word;
    }

    .udm-readonly-value--muted { color: #94a3b8; }

    .udm-token-row {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    .udm-token {
        font-family: 'SFMono-Regular', Consolas, monospace;
        font-size: 0.78rem;
        background: #f1f5f9;
        padding: 0.25rem 0.55rem;
        border-radius: 6px;
        color: #0f172a;
        word-break: break-all;
    }

    .udm-switch-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.85rem;
        padding: 0.85rem;
        background: #f8fafc;
        border-radius: 10px;
        border: 1px solid #e2e8f0;
        margin-top: 0.85rem;

        .p-inputswitch.p-inputswitch-checked .p-inputswitch-slider {
            background: #00BA6E !important;
        }
    }

    .udm-switch-text { display: flex; flex-direction: column; gap: 0.15rem; }
    .udm-switch-title { font-size: 0.85rem; font-weight: 600; color: #0f172a; }
    .udm-switch-help { font-size: 0.72rem; color: #64748b; }

    .udm-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.6rem;
        padding-top: 1rem;
        margin-top: 1.2rem;
        border-top: 1px solid #e2e8f0;
        flex-wrap: wrap;
    }

    .udm-callout {
        display: flex;
        gap: 0.55rem;
        background: #e0f7fd;
        border-left: 3px solid #34C6F1;
        border-radius: 10px;
        padding: 0.7rem 0.85rem;
        font-size: 0.78rem;
        color: #0f172a;
        margin: 0.75rem 0;

        i { color: #34C6F1; font-size: 0.95rem; flex-shrink: 0; margin-top: 0.1rem; }
    }

    .udm-current-role {
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
        padding: 0.85rem;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        margin-bottom: 1rem;
    }

    .udm-current-role-desc {
        font-size: 0.78rem;
        color: #64748b;
        line-height: 1.4;
    }

    /* ─── Permissions matrix ─────────────────────────────── */
    .udm-perm-table {
        width: 100%;
        border-collapse: collapse;
    }

    .udm-perm-cat {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
        background: #f8fafc;
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #e2e8f0;
        border-top: 1px solid #e2e8f0;
    }

    .udm-perm-table th {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
        text-align: left;
        padding: 0.5rem 0.75rem;
        background: #ffffff;
        border-bottom: 1px solid #e2e8f0;
    }

    .udm-perm-table th.udm-perm-th-checkbox { text-align: center; width: 130px; }

    .udm-perm-table td {
        padding: 0.55rem 0.75rem;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
        font-size: 0.82rem;
    }

    .udm-perm-table td.udm-perm-td-checkbox { text-align: center; }

    .udm-perm-label { font-weight: 600; color: #0f172a; font-size: 0.82rem; }
    .udm-perm-desc { font-size: 0.72rem; color: #64748b; margin-top: 0.15rem; }

    .udm-perm-checkbox-disabled .p-checkbox-box {
        background: #ffffff !important;
        border-color: #d1d5db !important;
        cursor: not-allowed !important;
    }
    .udm-perm-checkbox-disabled .p-checkbox-box.p-highlight {
        background: #d1fae5 !important;
        border-color: #00BA6E !important;
    }
    .udm-perm-checkbox-disabled .p-checkbox-icon { color: #00BA6E !important; }

    .udm-perm-footer {
        font-size: 0.72rem;
        color: #94a3b8;
        padding: 0.85rem 0 0;
    }

    /* ─── Timeline ─────────────────────────────── */
    .udm-history-filters {
        display: flex;
        gap: 0.4rem;
        margin-bottom: 0.85rem;
        flex-wrap: wrap;
    }

    .udm-filter-chip {
        font-family: inherit;
        font-size: 0.74rem;
        font-weight: 600;
        padding: 0.3rem 0.85rem;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #475569;
        cursor: pointer;
        transition: all 0.15s;
    }
    .udm-filter-chip:hover { background: #f8fafc; border-color: #cbd5e1; }
    .udm-filter-chip--active {
        background: #00BA6E;
        border-color: #00BA6E;
        color: #ffffff;
    }
    .udm-filter-chip--active:hover { background: #00a861; border-color: #00a861; }

    .udm-timeline {
        position: relative;
        padding-left: 1.4rem;
    }

    .udm-timeline::before {
        content: '';
        position: absolute;
        top: 0.4rem;
        bottom: 0.4rem;
        left: 0.35rem;
        width: 2px;
        background: #e2e8f0;
    }

    .udm-timeline-item {
        position: relative;
        padding-bottom: 1.2rem;
    }

    .udm-timeline-dot {
        position: absolute;
        left: -1.4rem;
        top: 0.25rem;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #204B7B;
        box-shadow: 0 0 0 3px #ffffff;
    }
    .udm-timeline-dot--perm { background: #00BA6E; }

    .udm-timeline-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .udm-timeline-title {
        font-size: 0.84rem;
        font-weight: 600;
        color: #0f172a;
    }
    .udm-timeline-arrow { color: #34C6F1; margin: 0 0.3rem; }

    .udm-timeline-date {
        font-size: 0.7rem;
        color: #94a3b8;
        font-weight: 500;
    }

    .udm-timeline-actor {
        font-size: 0.74rem;
        color: #64748b;
        margin-top: 0.15rem;
    }

    .udm-timeline-reason {
        margin-top: 0.45rem;
        padding: 0.5rem 0.7rem;
        border-left: 3px solid #e2e8f0;
        background: #f8fafc;
        font-style: italic;
        color: #475569;
        font-size: 0.78rem;
        line-height: 1.45;
        border-radius: 0 6px 6px 0;
    }

    .udm-loading,
    .udm-empty-tab {
        text-align: center;
        padding: 2rem 1rem;
        color: #94a3b8;
        font-size: 0.85rem;

        i { display: block; font-size: 1.4rem; margin-bottom: 0.4rem; opacity: 0.55; }
    }

    @media (max-width: 650px) {
        .udm-header { flex-wrap: wrap; }
        .udm-grid-2 { grid-template-columns: 1fr; }
        .udm-perm-table th.udm-perm-th-checkbox { width: 90px; }
    }
`;

export const userDetailDialogStyle = (isMobile) => isMobile ? ({
    width: '100vw',
    height: '90vh',
    margin: 0,
    borderRadius: '0.8rem 0.8rem 0 0',
    position: 'fixed',
    bottom: 0,
}) : ({
    width: '720px',
    maxWidth: '94vw',
    borderRadius: '0.8rem',
});

/* ───────────────────────── Create User Modal ───────────────────────── */
export const createUserModalCSS = () => css`
    font-family: 'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #0f172a;

    .cum-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.85rem;
        padding-bottom: 0.85rem;
        border-bottom: 1px solid #e2e8f0;
        margin-bottom: 1rem;
    }

    .cum-title {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 700;
        color: #0f172a;
        letter-spacing: 0.01em;
    }

    .cum-subtitle {
        margin: 0.2rem 0 0;
        font-size: 0.78rem;
        color: #64748b;
    }

    .cum-close {
        background: transparent;
        border: none;
        cursor: pointer;
        color: #94a3b8;
        font-size: 1.05rem;
        padding: 0.3rem 0.5rem;
    }
    .cum-close:hover { color: #475569; }

    .cum-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.85rem 1rem;
        margin-bottom: 0.5rem;

        @media (max-width: 650px) {
            grid-template-columns: 1fr;
        }
    }

    .cum-readonly {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        padding: 0.7rem 0.85rem;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
    }
    .cum-readonly-label {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #475569;
    }
    .cum-readonly-value {
        font-size: 0.9rem;
        font-weight: 600;
        color: #0f172a;
    }
    .cum-readonly-help {
        font-size: 0.72rem;
        color: #64748b;
        line-height: 1.4;
    }

    .cum-switch-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.85rem;
        padding: 0.85rem;
        background: #f8fafc;
        border-radius: 10px;
        border: 1px solid #e2e8f0;
        margin: 0.85rem 0 0;

        .p-inputswitch.p-inputswitch-checked .p-inputswitch-slider {
            background: #00BA6E !important;
        }
    }
    .cum-switch-text { display: flex; flex-direction: column; gap: 0.2rem; max-width: 80%; }
    .cum-switch-title { font-size: 0.85rem; font-weight: 600; color: #0f172a; }
    .cum-switch-help { font-size: 0.72rem; color: #64748b; line-height: 1.4; }

    /* ─── Resultado: credenciales ─── */
    .cum-credentials {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
        padding: 1rem;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        margin-bottom: 0.85rem;
    }
    .cum-cred-row { display: flex; flex-direction: column; gap: 0.3rem; }
    .cum-cred-label {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
    }
    .cum-cred-value {
        font-size: 0.9rem;
        font-weight: 600;
        color: #0f172a;
        word-break: break-all;
    }

    .cum-pass-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
    .cum-pass-value {
        font-family: 'SFMono-Regular', Consolas, 'Courier New', monospace;
        font-size: 1rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        background: #ffffff;
        padding: 0.55rem 0.85rem;
        border-radius: 8px;
        border: 1px solid #cbd5e1;
        color: #0f172a;
        flex: 1;
        min-width: 200px;
    }

    .cum-copy-all {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-family: inherit;
        font-size: 0.78rem;
        font-weight: 600;
        background: #ffffff;
        border: 1px dashed #cbd5e1;
        color: #0f172a;
        padding: 0.55rem 0.85rem;
        border-radius: 8px;
        cursor: pointer;
        align-self: flex-start;
        transition: all 0.15s;
    }
    .cum-copy-all:hover {
        border-color: #00BA6E;
        color: #00BA6E;
    }

    .cum-callout {
        display: flex;
        gap: 0.55rem;
        background: #e0f7fd;
        border-left: 3px solid #34C6F1;
        border-radius: 10px;
        padding: 0.7rem 0.85rem;
        font-size: 0.78rem;
        color: #0f172a;
        line-height: 1.4;
        margin-bottom: 0.85rem;

        i { color: #34C6F1; font-size: 0.95rem; flex-shrink: 0; margin-top: 0.1rem; }
    }
    .cum-callout--success {
        background: #e6f9f1;
        border-left-color: #00BA6E;
        i { color: #00BA6E; }
    }
    .cum-callout--warn {
        background: #fef3c7;
        border-left-color: #f59e0b;
        i { color: #f59e0b; }
    }

    .cum-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.6rem;
        padding-top: 1rem;
        margin-top: 1rem;
        border-top: 1px solid #e2e8f0;
        flex-wrap: wrap;
    }
`;

export const createUserDialogStyle = (isMobile) => isMobile ? ({
    width: '100vw',
    height: 'auto',
    maxHeight: '92vh',
    margin: 0,
    borderRadius: '0.8rem 0.8rem 0 0',
    position: 'fixed',
    bottom: 0,
}) : ({
    width: '560px',
    maxWidth: '94vw',
    borderRadius: '0.8rem',
});
